﻿import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import { Loader2 } from 'lucide-react';
import type { ApiError } from '@pms/shared';
import {
  assetsService,
  type AssetCategory,
  type AssetSubcategory,
} from '@/services/assets.service';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

function errMsg(e: unknown): string {
  return (e as AxiosError<ApiError>).response?.data?.error?.message ?? 'Something went wrong';
}

interface FormValues {
  category_id: string;
  name: string;
  code: string;
  description: string;
}

interface Props {
  open: boolean;
  mode: 'create' | 'edit';
  subcategory?: AssetSubcategory;
  preselectedCategoryId?: string;
  categories: AssetCategory[];
  onClose: () => void;
  onSuccess: () => void;
}

export function SubcategoryFormDialog({
  open,
  mode,
  subcategory,
  preselectedCategoryId,
  categories,
  onClose,
  onSuccess,
}: Props) {
  const isEdit = mode === 'edit';
  const queryClient = useQueryClient();

  const { register, handleSubmit, reset, setValue, control, formState: { errors } } = useForm<FormValues>({
    defaultValues: {
      category_id: preselectedCategoryId ?? '',
      name: '',
      code: '',
      description: '',
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        category_id: subcategory?.category_id ?? preselectedCategoryId ?? '',
        name: subcategory?.name ?? '',
        code: subcategory?.code ?? '',
        description: subcategory?.description ?? '',
      });
    }
  }, [open, subcategory, preselectedCategoryId, reset]);

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const input = {
        category_id: values.category_id,
        name: values.name.trim(),
        code: values.code.trim().toUpperCase(),
        description: values.description.trim() || undefined,
      };
      if (isEdit && subcategory) {
        await assetsService.updateSubcategory(subcategory.id, input);
      } else {
        await assetsService.createSubcategory(input);
      }
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Subcategory updated' : 'Subcategory created');
      void queryClient.invalidateQueries({ queryKey: ['assets', 'subcategories'] });
      void queryClient.invalidateQueries({ queryKey: ['assets', 'categories'] });
      onSuccess();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const activeCategories = categories.filter((c) => c.is_active !== false);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-full max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Subcategory' : 'New Subcategory'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Category *</Label>
            <Controller
              control={control}
              name="category_id"
              rules={{ required: 'Category is required' }}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {activeCategories.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {errors.category_id && <p className="text-xs text-destructive">{errors.category_id.message}</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="subcat-name">Subcategory Name *</Label>
            <Input
              id="subcat-name"
              {...register('name', { required: 'Name is required' })}
              placeholder="e.g. Laptops"
            />
            {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="subcat-code">Subcategory Code *</Label>
            <Input
              id="subcat-code"
              {...register('code', { required: 'Code is required', maxLength: { value: 20, message: 'Max 20 characters' } })}
              onChange={(e) => setValue('code', e.target.value.toUpperCase())}
              placeholder="e.g. LP"
              maxLength={20}
            />
            {errors.code && <p className="text-xs text-destructive">{errors.code.message}</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="subcat-desc">Description</Label>
            <Textarea
              id="subcat-desc"
              {...register('description')}
              placeholder="Optional description"
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {isEdit ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
